import React, { useState, useEffect, useCallback } from 'react';
import PersonWorkplacesComponent from './PersonWorkplacesComponent';
import { PERSON_WORKPLACES_QUERY, PROFESSIONS_QUERY, POSITIONS_QUERY, EMPLOYERS_QUERY } from './queries';
import { ADD_UPDATE_PERSON_WORKPLACES } from './mutations';
import { PERSON_WORKPLACES_UPDATED } from './subscriptions';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { useMutation, useSubscription } from '@apollo/client';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useToast } from 'utils/hooks';
import { useDirtyFieldValidation } from "@totalsoft/pure-validations-react";
import { validatorPersonWorkplaces } from "./validation"

const PersonWorkplacesContainer = ({ partnerId, documentId, siteId, clientPersonworkplacesWrite }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const [personWorkplacesState, setPersonWorkplacesState] = useState([]);
  const [saveDisabled, setSaveDisabled] = useState(true);

  const [validationPersonWorkplaces, validatePersonWorkplaces] = useDirtyFieldValidation(validatorPersonWorkplaces);

  useEffect(() => {
    validatePersonWorkplaces(personWorkplacesState);
  }, [personWorkplacesState, validatePersonWorkplaces]);

  const { data: personWorkplaces, loading: loadingPersonWorkplaces } = useQueryWithErrorHandling(PERSON_WORKPLACES_QUERY, {
    variables: { partnerId, documentId, siteId },
    onCompleted: data => {
      setPersonWorkplacesState(data.personWorkplaces);
    }
  });

  const { data: professions, loading: loadingProfessions } = useQueryWithErrorHandling(PROFESSIONS_QUERY);

  const { data: positions, loading: loadingPositions } = useQueryWithErrorHandling(POSITIONS_QUERY);

  const { data: employers, loading: loadingEmployers } = useQueryWithErrorHandling(EMPLOYERS_QUERY);

  const [addOrUpdatePersonWorkplaces] = useMutation(ADD_UPDATE_PERSON_WORKPLACES, {
    onError: error => console.log('error', error)
  });

  useSubscription(PERSON_WORKPLACES_UPDATED, {
    onSubscriptionData: ({ _, client }) => {
      addToast(t('OfferEdit.Client.PersonWorkplaces.Updated'), 'info');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'personWorkplaces' });
      client.cache.gc();
    }
  });

  const onAdd = useCallback(() => {
    setPersonWorkplacesState([
      ...personWorkplacesState,
      {
        personWorkplaceId: null,
        professionId: null,
        positionId: null,
        employerId: null,
        dateFrom: null,
        dateTo: null
      }
    ]);
  }, [personWorkplacesState]);

  const onModify = useCallback(
    (event, field, idx) => {
      const localPersonWorkplacesState = [...personWorkplacesState];
      localPersonWorkplacesState[idx] = {...localPersonWorkplacesState[idx], [field]: event};
      setPersonWorkplacesState(localPersonWorkplacesState);
      setSaveDisabled(false);
    },
    [personWorkplacesState]
  );

  const onSave = useCallback(() => {
    addOrUpdatePersonWorkplaces({
      variables: { input: { partnerId: partnerId, personWorkplaces: personWorkplacesState } }
    });
    setSaveDisabled(true);
  }, [addOrUpdatePersonWorkplaces, partnerId, personWorkplacesState]);

  const onDelete = useCallback(
    idx => {
      const localPersonWorkplacesState = [...personWorkplacesState];
      localPersonWorkplacesState.splice(idx, 1);
      setPersonWorkplacesState(localPersonWorkplacesState);
      setSaveDisabled(false);
    },
    [personWorkplacesState]
  );

  const loadedData = () =>
    !loadingPersonWorkplaces &&
    personWorkplaces &&
    !loadingProfessions &&
    professions &&
    !loadingPositions &&
    positions &&
    !loadingEmployers &&
    employers;

  return (
    loadedData && (
      <PersonWorkplacesComponent
        personWorkplaces={personWorkplacesState}
        professionData={professions?.professions}
        positions={positions?.positions}
        employers={employers?.employers}
        saveDisabled={saveDisabled}
        onAdd={onAdd}
        onModify={onModify}
        onSave={onSave}
        onDelete={onDelete}
        validationPersonWorkplaces={validationPersonWorkplaces}
        clientPersonworkplacesWrite={clientPersonworkplacesWrite}
      />
    )
  );
};
PersonWorkplacesContainer.propTypes = {
  partnerId: PropTypes.number.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  clientPersonworkplacesWrite: PropTypes.bool.isRequired
};
export default PersonWorkplacesContainer;
